/**
 * POST-AUTH REDIRECT
 *
 * The Google OAuth button stores the intended destination in sessionStorage
 * ('listbright_post_auth_dest') because the browser is fully redirected away
 * during sign-in. When the session lands back on the site, this listener
 * consumes that value once and sends the user where they were headed
 * (e.g. the create flow or a deep link), instead of leaving them on '/'.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

const DEST_KEY = 'listbright_post_auth_dest';

const isSafePath = (value: string | null): value is string =>
  !!value && value.startsWith('/') && !value.startsWith('//');

export function PostAuthRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    const consume = () => {
      let dest: string | null = null;
      try {
        dest = sessionStorage.getItem(DEST_KEY);
        if (dest) sessionStorage.removeItem(DEST_KEY);
      } catch {
        return;
      }
      if (isSafePath(dest) && dest !== window.location.pathname + window.location.search) {
        navigate(dest, { replace: true });
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) consume();
    });

    // Also catch the case where the session was already hydrated by the time
    // this component mounts (e.g. tokens processed before React subscribed).
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) consume();
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  return null;
}
